import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { BADGE_TIERS, BADGE_CATEGORIES, tierMeta } from "@/lib/badgeMeta";
import { imageUrl } from "@/pages/Items";
import { Plus, Edit3, Trash2, Save, X, Upload, Image as ImageIcon, ExternalLink, Award } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]+/g, "");

const EMPTY_BADGE = {
  slug: "", name: "", image_url: "", tier: "Bronze", category: "Special",
  description: "", requirement: "", reward: "",
};

export default function BadgesTab() {
  const [params, setParams] = useSearchParams();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [newMode, setNewMode] = useState(false);

  const editSlug = params.get("edit");

  useEffect(() => {
    client.get("/badges")
      .then(r => { setBadges(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [reloadKey]);

  const editing = newMode ? EMPTY_BADGE : (editSlug ? badges.find(b => b.slug === editSlug) : null);

  const startNew = () => setNewMode(true);
  const closeEditor = () => {
    setNewMode(false);
    if (editSlug) {
      const sp = new URLSearchParams(params);
      sp.delete("edit");
      setParams(sp);
    }
  };
  const onSaved = () => { closeEditor(); setReloadKey(k => k + 1); };
  const onDelete = async (slug) => {
    if (!window.confirm(`Delete badge "${slug}"?`)) return;
    await client.delete(`/badges/${slug}`);
    setReloadKey(k => k + 1);
  };

  if (editing) {
    return <BadgeEditor initial={editing} onCancel={closeEditor} onSaved={onSaved} />;
  }

  return (
    <div data-testid="badges-tab">
      <div className="flex justify-between items-center mb-5 gap-3">
        <p className="text-sm text-[var(--t-text-dim)]">{badges.length} badges in the hall.</p>
        <button onClick={startNew} className="btn-primary !py-2 !px-4 !text-xs" data-testid="new-badge-btn">
          <Plus className="w-4 h-4" /> New Badge
        </button>
      </div>

      <BadgesAdminBody loading={loading} badges={badges} onEdit={(slug) => setParams({ tab: "badges", edit: slug })} onDelete={onDelete} />
    </div>
  );
}

function BadgesAdminBody({ loading, badges, onEdit, onDelete }) {
  if (loading) {
    return <div className="text-center py-10 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow">// loading...</div>;
  }
  if (badges.length === 0) {
    return <div className="panel p-10 text-center text-[var(--t-text-muted)] font-mono text-xs tracking-[0.2em] uppercase">// hall is empty · click new badge to start</div>;
  }
  return (
    <div className="panel divide-y divide-[var(--t-border)]">
      {badges.map(b => <BadgeAdminRow key={b.slug} badge={b} onEdit={onEdit} onDelete={onDelete} />)}
    </div>
  );
}

function BadgeAdminRow({ badge: b, onEdit, onDelete }) {
  const t = tierMeta(b.tier);
  const img = imageUrl(b.image_url);
  return (
    <div className="flex items-center gap-4 p-3 hover:bg-[rgba(58,160,255,0.04)]" data-testid={`admin-badge-${b.slug}`}>
      <div className="w-14 h-14 flex-shrink-0 panel overflow-hidden" style={{ borderColor: t.color }}>
        {img
          ? <img src={img} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Award className="w-5 h-5 text-[var(--t-text-muted)]" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block px-2 py-0.5 text-[9px] font-mono tracking-[0.18em] uppercase border ${t.chip}`}>{b.tier}</span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">{b.category}</span>
        </div>
        <div className="font-display text-base text-[var(--t-cyan)] uppercase mt-0.5 leading-tight">{b.name}</div>
        <div className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.15em] mt-0.5 truncate">/{b.slug}</div>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <a href={`/wiki/badges/${b.slug}`} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 !px-2.5 !text-[10px]" data-testid={`view-badge-${b.slug}`}><ExternalLink className="w-3 h-3" /></a>
        <button onClick={() => onEdit(b.slug)} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]" data-testid={`edit-badge-${b.slug}`}><Edit3 className="w-3 h-3" /></button>
        <button onClick={() => onDelete(b.slug)} className="btn-ghost !py-1.5 !px-2.5 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid={`delete-badge-${b.slug}`}><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

function BadgeEditor({ initial, onCancel, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    slug: initial.slug || "",
    name: initial.name || "",
    image_url: initial.image_url || "",
    tier: initial.tier || "Bronze",
    category: initial.category || "Special",
    description: initial.description || "",
    requirement: initial.requirement || "",
    reward: initial.reward || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const setField = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === "name" && isNew && !f.slug) next.slug = slugify(v);
      return next;
    });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("image_url", r.data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setError("");
    if (!form.name.trim()) { setError("Name is required."); return; }
    const payload = { ...form, slug: slugify(form.slug || form.name) };
    setSaving(true);
    try {
      await client.post("/badges", payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const t = tierMeta(form.tier);
  const previewImg = imageUrl(form.image_url);

  return (
    <div data-testid="badge-editor">
      <div className="flex items-center justify-between mb-5 gap-3">
        <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase">{isNew ? "New Badge" : "Edit Badge"}</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="badge-cancel"><X className="w-3.5 h-3.5" /> Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-4 !text-xs" data-testid="badge-save"><Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}</button>
        </div>
      </div>

      {error && <div className="panel p-3 mb-4 text-sm text-[var(--t-red)] font-mono" data-testid="badge-error">{error}</div>}

      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-3">
          <div className="aspect-square panel overflow-hidden" style={{ borderColor: t.color, boxShadow: `0 0 24px ${t.glow}` }}>
            {previewImg
              ? <img src={previewImg} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center bg-[#050912]"><Award className="w-16 h-16 text-[var(--t-text-muted)]" /></div>}
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary w-full !justify-center !py-2 !text-xs" data-testid="badge-upload-btn">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Upload Image"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="badge-file-input" />
          <input className="field !text-xs" placeholder="…or paste image URL" value={form.image_url} onChange={(e) => setField("image_url", e.target.value)} data-testid="badge-image-url" />
          {form.image_url && (
            <button onClick={() => setField("image_url", "")} className="text-[10px] font-mono tracking-[0.2em] text-[var(--t-text-muted)] uppercase hover:text-[var(--t-red)]" data-testid="badge-clear-image">Remove image</button>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Name *</label>
              <input className="field" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Conqueror of Asia" data-testid="badge-name" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Slug</label>
              <input className="field font-mono text-sm" value={form.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="conqueror-of-asia" disabled={!isNew} data-testid="badge-slug" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Tier</label>
              <select className="field" value={form.tier} onChange={(e) => setField("tier", e.target.value)} data-testid="badge-tier">
                {BADGE_TIERS.map(t => <option key={t.key} value={t.key}>{t.key}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Category</label>
              <select className="field" value={form.category} onChange={(e) => setField("category", e.target.value)} data-testid="badge-category">
                {BADGE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Description (markdown)</label>
            <textarea className="field" rows={4} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Lore, prestige, story behind the badge..." data-testid="badge-description" />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Unlock Requirement (markdown)</label>
            <textarea className="field" rows={3} value={form.requirement} onChange={(e) => setField("requirement", e.target.value)} placeholder="e.g. Occupy 10 enemy regions in a single week." data-testid="badge-requirement-input" />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Reward (markdown)</label>
            <textarea className="field" rows={3} value={form.reward} onChange={(e) => setField("reward", e.target.value)} placeholder="e.g. +5% damage vs occupied regions, profile flair." data-testid="badge-reward-input" />
          </div>
        </div>
      </div>
    </div>
  );
}
